/**
 * AskChefVoicesPage — the approval page for Annie's and Chef Dominic's voices.
 *
 * Super admin only. Every reply shown here is an ACTUAL reply from the same
 * pipeline a customer hits (services/chef.answer) — nothing is scripted. The
 * character sheet underneath it (traits, signature phrases, never-say list,
 * greetings) is editable and takes effect on the next reply.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2, Play, Save, Sparkles } from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';

const ORG = 'grocery-demo-org-0001';
const LANGS = ['en', 'fr', 'es'];
const SOURCE_LABEL = {
    cache: 'free · already known', saved: 'free · saved recipe',
    identity: 'free · app answer', check_label: 'free · app answer',
    model: 'model call', fallback: 'fallback', limit: 'allowance reached',
};

const Reply = ({ row }) => (
    <div className="space-y-1.5 rounded-2xl border border-slate-200 p-3 dark:border-slate-700"
        data-testid={`voice-row-${row.language}-${row.persona}-${row.scenario}`}>
        <div className="flex flex-wrap items-center gap-2 text-[11px] font-bold uppercase tracking-wide">
            <span className="rounded-full bg-slate-900 px-2 py-0.5 text-white dark:bg-white dark:text-slate-900">
                {row.scenario.replace(/_/g, ' ')}
            </span>
            <span className="text-muted-foreground">{SOURCE_LABEL[row.source] || row.source}</span>
            {row.opinion ? (
                <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-200"
                    data-testid={`voice-opinion-${row.language}-${row.persona}`}>AI opinion</span>
            ) : null}
            {row.kid ? <span className="rounded-full bg-amber-100 px-2 py-0.5 text-amber-800">kid view</span> : null}
            {row.words ? (
                <span className="text-muted-foreground" data-testid={`voice-words-${row.language}-${row.persona}-${row.scenario}`}>
                    {row.words} words
                </span>
            ) : null}
            {(row.english_words || []).length ? (
                <span className="rounded-full bg-rose-100 px-2 py-0.5 text-rose-700"
                    data-testid={`voice-english-${row.language}-${row.persona}-${row.scenario}`}>
                    English: {row.english_words.join(', ')}
                </span>
            ) : null}
            {row.handoff_to ? (
                <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-emerald-800">
                    hand-off → {row.handoff_to}
                </span>
            ) : null}
        </div>
        <p className="text-sm font-semibold text-foreground">{row.question}</p>
        <p className="whitespace-pre-wrap rounded-xl bg-slate-50 px-3 py-2 text-sm text-foreground dark:bg-slate-800/60">
            {row.error ? `⚠ ${row.error}` : row.reply}
        </p>
        {row.recipe_title ? (
            <p className="text-[11px] text-muted-foreground">recipe card: {row.recipe_title}</p>
        ) : null}
    </div>
);

const CharacterSheet = ({ personaKey, data, onChange }) => (
    <div className="space-y-3 rounded-2xl border border-slate-200 p-4 dark:border-slate-700"
        data-testid={`voice-sheet-${personaKey}`}>
        <div className="flex items-center gap-2">
            <img src={data.avatar} alt="" aria-hidden="true" className="h-10 w-10" />
            <Input value={data.name} data-testid={`voice-name-${personaKey}`}
                onChange={(e) => onChange({ ...data, name: e.target.value })}
                className="max-w-xs min-h-[44px] font-bold" />
        </div>
        <label className="block text-xs font-bold uppercase tracking-wide text-muted-foreground">
            Five traits (comma separated)
        </label>
        <Input value={(data.traits || []).join(', ')} data-testid={`voice-traits-${personaKey}`}
            onChange={(e) => onChange({ ...data, traits: e.target.value.split(',').map((x) => x.trim()).filter(Boolean) })}
            className="min-h-[44px]" />
        <label className="block text-xs font-bold uppercase tracking-wide text-muted-foreground">
            Never say (comma separated)
        </label>
        <Input value={(data.never_say || []).join(', ')} data-testid={`voice-neversay-${personaKey}`}
            onChange={(e) => onChange({ ...data, never_say: e.target.value.split(',').map((x) => x.trim()).filter(Boolean) })}
            className="min-h-[44px]" />
        {LANGS.map((lang) => (
            <div key={lang} className="space-y-1">
                <label className="block text-xs font-bold uppercase tracking-wide text-muted-foreground">
                    Eight signature phrases · {lang.toUpperCase()} (one per line)
                </label>
                <Textarea rows={4} value={(data.phrases?.[lang] || []).join('\n')}
                    data-testid={`voice-phrases-${personaKey}-${lang}`}
                    onChange={(e) => onChange({ ...data, phrases: {
                        ...data.phrases, [lang]: e.target.value.split('\n').map((x) => x.trim()).filter(Boolean) } })} />
            </div>
        ))}
    </div>
);

const VoiceBank = ({ bank }) => {
    if (!bank) return null;
    return (
        <section className="space-y-4" data-testid="voice-bank">
            <h2 className="text-lg font-bold text-foreground">Voice bank — every line the app says</h2>
            <p className="text-sm text-muted-foreground">
                French is written in <strong>tu</strong> and the vous column is generated from the
                same line, so the two forms can never drift. This organization is set to
                <strong> {bank.french_address}</strong>. Hand-off pronouns: Annie
                = {bank.pronouns?.annie?.pronoun}, Chef Dominic = {bank.pronouns?.dominic?.pronoun}.
            </p>

            <div className="space-y-2" data-testid="voice-bank-fr-samples">
                <h3 className="text-base font-bold text-foreground">
                    French side by side ({(bank.fr_samples || []).length} samples)
                </h3>
                {(bank.fr_samples || []).map((s, i) => (
                    <div key={i} className="rounded-xl border border-slate-200 p-3 text-sm dark:border-slate-700"
                        data-testid={`voice-bank-sample-${i}`}>
                        <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
                            {s.case.replace(/_/g, ' ')} · {s.persona}
                        </p>
                        <p className="text-foreground"><strong>tu</strong> — {s.tu}</p>
                        <p className="text-muted-foreground"><strong>vous</strong> — {s.vous}</p>
                    </div>
                ))}
            </div>

            {Object.entries(bank.cases || {}).map(([caseKey, personas]) => (
                <details key={caseKey} className="rounded-2xl border border-slate-200 p-3 dark:border-slate-700"
                    data-testid={`voice-bank-case-${caseKey}`}>
                    <summary className="cursor-pointer text-base font-bold text-foreground">
                        {caseKey.replace(/_/g, ' ')}
                    </summary>
                    {Object.entries(personas).map(([p, langs]) => (
                        <div key={p} className="mt-2 space-y-1">
                            <p className="text-sm font-bold text-foreground">{p}</p>
                            {LANGS.map((l) => (
                                <ol key={l} className="list-inside list-decimal space-y-0.5 text-xs text-muted-foreground">
                                    <li className="list-none font-bold uppercase">{l}</li>
                                    {(langs[l] || []).map((row, i) => (
                                        <li key={i} data-testid={`voice-bank-${caseKey}-${p}-${l}-${i}`}>
                                            {row.line} <span className="opacity-60">({row.words} words)</span>
                                        </li>
                                    ))}
                                </ol>
                            ))}
                        </div>
                    ))}
                </details>
            ))}
        </section>
    );
};

export default function AskChefVoicesPage() {
    const { t } = useTranslation();
    const [runs, setRuns] = useState([]);
    const [personas, setPersonas] = useState(null);
    const [bank, setBank] = useState(null);
    const [busy, setBusy] = useState(false);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        const [pv, ps, vb] = await Promise.all([
            api.get('/ask-chef/admin/voice-preview'),
            api.get('/ask-chef/admin/personas', { params: { organization_id: ORG } }),
            api.get('/ask-chef/admin/voice-bank', { params: { organization_id: ORG } }),
        ]);
        setRuns(pv.data.runs || []);
        setPersonas(ps.data.personas);
        setBank(vb.data);
    }, []);
    useEffect(() => { load().catch(() => toast.error('Super admin only')); }, [load]);

    const run = async () => {
        setBusy(true);
        try {
            // One request per language AND persona: a single call for all six
            // combinations takes longer than the gateway allows.
            let total = 0;
            for (const lang of LANGS) {
                for (const persona of ['annie', 'dominic']) {
                    const r = await api.post('/ask-chef/admin/voice-preview', {
                        organization_id: ORG, languages: [lang], personas: [persona],
                    }, { timeout: 180000 });
                    total += r.data.ran || 0;
                    await load();
                }
            }
            toast.success(`${total} replies generated`);
        } catch (_e) { toast.error('Could not run the preview'); } finally { setBusy(false); }
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.put('/ask-chef/admin/personas', { personas }, { params: { organization_id: ORG } });
            toast.success('Character sheet saved');
            await load();
        } catch (_e) { toast.error('Could not save'); } finally { setSaving(false); }
    };

    return (
        <div className="mx-auto max-w-5xl space-y-6 px-4 py-6" data-testid="ask-chef-voices-page">
            <header className="space-y-1">
                <h1 className="flex items-center gap-2 text-2xl font-black text-foreground">
                    <Sparkles className="h-6 w-6 text-indigo-500" />
                    Ask your chef — voice preview
                </h1>
                <p className="text-sm text-muted-foreground">
                    Twelve real situations per persona, per language. These are the actual replies
                    the live pipeline produced, not samples written by hand. Approve them or edit
                    the character sheets below and run it again.
                </p>
            </header>

            <Button onClick={run} disabled={busy} data-testid="voice-run-btn"
                className="min-h-[52px] gap-2 font-bold">
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Play className="h-5 w-5" />}
                Run the twelve situations (EN + FR + ES)
            </Button>

            <a href="/admin/ask-chef-review" data-testid="voice-review-link"
                className="block text-sm font-bold text-indigo-600 underline dark:text-indigo-300">
                Review the shared recipes and featured suggestions →
            </a>

            {['en', 'fr', 'es'].map((lang) => {
                const forLang = runs.filter((r) => r.language === lang);
                if (!forLang.length) return null;
                return (
                    <section key={lang} className="space-y-4" data-testid={`voice-lang-${lang}`}>
                        <h2 className="text-lg font-bold text-foreground">{lang.toUpperCase()}</h2>
                        <div className="grid gap-4 md:grid-cols-2">
                            {['annie', 'dominic'].map((p) => (
                                <div key={p} className="space-y-2">
                                    <h3 className="text-base font-bold text-foreground">
                                        {personas?.[p]?.name || p}
                                    </h3>
                                    {forLang.filter((r) => r.persona === p)
                                        .map((r) => <Reply key={r.scenario} row={r} />)}
                                </div>
                            ))}
                        </div>
                    </section>
                );
            })}

            <VoiceBank bank={bank} />

            {personas ? (
                <section className="space-y-4" data-testid="voice-sheets">
                    <h2 className="text-lg font-bold text-foreground">Character sheets</h2>
                    <div className="grid gap-4 md:grid-cols-2">
                        {['annie', 'dominic'].map((p) => (
                            <CharacterSheet key={p} personaKey={p} data={personas[p]}
                                onChange={(next) => setPersonas((all) => ({ ...all, [p]: next }))} />
                        ))}
                    </div>
                    <Button onClick={save} disabled={saving} data-testid="voice-save-btn"
                        className="min-h-[52px] gap-2 font-bold">
                        {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />}
                        Save the character sheets
                    </Button>
                </section>
            ) : null}
        </div>
    );
}
